/**
 * Checklist content is stored as editable HTML files in src/data/content/.
 * Edit hero.html and item-1.html through item-15.html to change copy.
 * Content is imported at build time so it always loads with the app.
 */

import type { ChecklistSection, ChecklistItem } from '../types';

import heroHtml from './content/hero.html';
import item1Html from './content/item-1.html';
import item2Html from './content/item-2.html';
import item3Html from './content/item-3.html';
import item4Html from './content/item-4.html';
import item5Html from './content/item-5.html';
import item6Html from './content/item-6.html';
import item7Html from './content/item-7.html';
import item8Html from './content/item-8.html';
import item9Html from './content/item-9.html';
import item10Html from './content/item-10.html';
import item11Html from './content/item-11.html';
import item12Html from './content/item-12.html';
import item13Html from './content/item-13.html';
import item14Html from './content/item-14.html';
import item15Html from './content/item-15.html';

export interface ChecklistContent {
  hero: string;
  sections: ChecklistSection[];
}

const itemContents = [
  item1Html,
  item2Html,
  item3Html,
  item4Html,
  item5Html,
  item6Html,
  item7Html,
  item8Html,
  item9Html,
  item10Html,
  item11Html,
  item12Html,
  item13Html,
  item14Html,
  item15Html,
];

const sectionConfig = [
  {
    id: 'section-1',
    number: 1,
    title: 'Documentation and context',
    timeToImplement: '6–8 weeks',
    items: [
      { id: 'item-1', number: 1, title: 'Machine-readable component metadata exists', difficulty: 'significant' as const, impact: 'High impact' },
      { id: 'item-2', number: 2, title: 'Usage examples are code-first, not screenshot-first', difficulty: 'medium' as const, impact: 'High impact' },
      { id: 'item-3', number: 3, title: 'Design decisions are documented with rationale', difficulty: 'medium' as const, impact: 'Medium impact' },
      { id: 'item-4', number: 4, title: 'llms.txt file exists in your repo', difficulty: 'quick' as const, impact: 'Medium impact' },
    ],
  },
  {
    id: 'section-2',
    number: 2,
    title: 'Component architecture',
    timeToImplement: '4–8 weeks',
    items: [
      { id: 'item-5', number: 5, title: 'Components have consistent naming conventions', difficulty: 'quick' as const, impact: 'High impact' },
      { id: 'item-6', number: 6, title: 'Props and variants follow semantic patterns', difficulty: 'medium' as const, impact: 'High impact' },
      { id: 'item-7', number: 7, title: 'Token architecture is three-tier minimum', difficulty: 'significant' as const, impact: 'High impact' },
      { id: 'item-8', number: 8, title: 'Component composition is explicit', difficulty: 'medium' as const, impact: 'Medium impact' },
    ],
  },
  {
    id: 'section-3',
    number: 3,
    title: 'Technical infrastructure',
    timeToImplement: '6–10 weeks',
    items: [
      { id: 'item-9', number: 9, title: 'Design tokens are in a machine-readable format', difficulty: 'significant' as const, impact: 'Critical foundation' },
      { id: 'item-10', number: 10, title: 'Figma components use Code Connect with MCP integration', difficulty: 'significant' as const, impact: 'High impact' },
      { id: 'item-11', number: 11, title: 'Component stories and examples are programmatically accessible', difficulty: 'medium' as const, impact: 'Medium impact' },
      { id: 'item-12', number: 12, title: 'Accessibility requirements are documented in structured format', difficulty: 'medium' as const, impact: 'High impact' },
    ],
  },
  {
    id: 'section-4',
    number: 4,
    title: 'Workflow integration',
    timeToImplement: '8–12 weeks',
    items: [
      { id: 'item-13', number: 13, title: 'Component usage can be tracked and queried', difficulty: 'significant' as const, impact: 'Medium impact' },
      { id: 'item-14', number: 14, title: 'Design system has a clear source of truth hierarchy', difficulty: 'medium' as const, impact: 'High impact' },
      { id: 'item-15', number: 15, title: 'Breaking changes have a documented migration path', difficulty: 'significant' as const, impact: 'High impact' },
    ],
  },
];

let contentIndex = 0;

export const checklistContent: ChecklistContent = {
  hero: heroHtml,
  sections: sectionConfig.map((section) => ({
    id: section.id,
    number: section.number,
    title: section.title,
    weight: '',
    timeToImplement: section.timeToImplement,
    items: section.items.map((item) => {
      const content = itemContents[contentIndex++] ?? '';
      return {
        ...item,
        section: section.id,
        content,
      } as ChecklistItem;
    }),
  })),
};
